import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Mail, ArrowLeft } from 'lucide-react';
import BillingPage from '@/components/billing/BillingPage';

import { useEntitlements } from '@/hooks/useEntitlements';
import { useAuth } from '@/contexts/AuthContext';
import { CONTACT } from '@/content/pricing';

/**
 * Hard block for organizations whose 30-day pilot ended and who never
 * subscribed. Organizations that DID pay and later lapsed are never blocked
 * here — they only get a warning banner (see PilotStatusBanner).
 */
export const LicenseGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userRole } = useAuth();
  const { licenseState, isLoading } = useEntitlements();
  const [showBilling, setShowBilling] = React.useState(false);

  if (isLoading || licenseState !== 'pilot_expired') return <>{children}</>;

  const canBuy = userRole === 'super_admin';

  if (canBuy && showBilling) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => setShowBilling(false)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <BillingPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="max-w-lg w-full p-8 space-y-5 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">
            Your pilot has ended
          </h1>
          <p className="text-sm text-muted-foreground">
            Your 30-day trial period is over. Choose a plan to restore access
            for your team. Your data is safe and will be exactly as you left it.
          </p>
        </div>
        {canBuy ? (
          <Button className="w-full" onClick={() => setShowBilling(true)}>
            Choose a plan
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Ask your organization administrator to choose a plan.
          </p>
        )}

        <Button variant="ghost" size="sm" asChild>
          <a href={`mailto:${CONTACT.sales}?subject=Pilot%20ended`}>
            <Mail className="h-4 w-4 mr-2" /> Contact us
          </a>
        </Button>
      </Card>
    </div>
  );
};

export default LicenseGate;
